import React, { Component } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { uniqBy } from "lodash";

import {
  Button,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Modal,
  Placeholder,
  Table,
} from "semantic-ui-react";

import { SetAdvisor, UpdateAdvisor } from "data/advisor/actions";
import { SetHub } from "data/hub/actions";
import { GetAdvisor } from "data/libs/advisor";
import { CreateHubAdvisor, DeletehubAdvisor, EditHub } from "data/libs/hubs";

import Access from "components/Access";
import { SetAlert } from "components/Alerts/actions";
import { FeatureFlag } from "components/FeatureFlag";
import PopupMenu from "components/PopupMenu";

import { AddHub } from "./AddHub";

class HubInformationForm extends Component {
  static defaultProps = {
    selectedHub: {},
  };

  static propTypes = {
    advisor: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      hubs: PropTypes.array.isRequired,
      id: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      practices: PropTypes.array.isRequired,
      role: PropTypes.object,
    }).isRequired,
    selectedHub: PropTypes.object,
    setAdvisor: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired,
    setHub: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      initialHubAdvisorIds: [],
      hubAdvisors: [],
      hubModalOpen: false,
      isEditingHub: false,
      loadingHubTable: false,
      name: "",
      popupOpenId: null,
    };
  }

  handleChange = (e, { name, value }) => {
    this.setState({ [name]: value });
  };

  reloadData = async () => {
    const { setAdvisor } = this.props;

    try {
      this.setState({ hubModalOpen: false, loadingHubTable: true });
      const advisorResponse = await GetAdvisor();
      setAdvisor(advisorResponse);
      this.setState({ loadingHubTable: false });
    } catch (error) {
      console.error(error);
      this.setState({ hubModalOpen: false, loadingHubTable: false });
    }
  };

  save = () => {
    const { selectedHub, setAlert, setHub, advisor } = this.props;
    const { id, initialHubAdvisorIds, hubAdvisors, name } = this.state;

    const advisorIdsToRemove = initialHubAdvisorIds.filter((i) => {
      return !hubAdvisors.includes(i);
    });
    const advisorIdsToAdd = hubAdvisors.filter((i) => {
      return !initialHubAdvisorIds.includes(i);
    });

    this.setState({ isEditingHub: true });
    EditHub(id, { name: name })
      .then((response) => {
        if (selectedHub && response.id === selectedHub.id) {
          setHub(response, advisor);
        }

        const promises = [];

        advisorIdsToRemove.forEach((advisorId) => {
          promises.push(DeletehubAdvisor(id, advisorId));
        });

        advisorIdsToAdd.forEach((advisorId) => {
          promises.push(CreateHubAdvisor(id, { id: advisorId }));
        });

        if (promises.length > 0) {
          Promise.all(promises)
            .then(this.reloadData)
            .catch((error) => {
              console.error(error);
              this.setState({ isEditingHub: false });
              setAlert({ type: "error", text: "Failed to update Hub users." });
            });
        } else {
          this.setState({ isEditingHub: false });
          this.reloadData();
        }
      })
      .catch((error) => {
        console.error(error);
        setAlert({ type: "error", text: "Failed to update Hub information." });
        this.setState({ isEditingHub: false });
      });
  };

  openHubModal = (hub) => {
    const hubAdvisors = hub.advisors.map((advisor) => {
      return advisor.id;
    });

    this.setState({
      hubAdvisors: hubAdvisors,
      hubModalOpen: true,
      initialHubAdvisorIds: [...hubAdvisors],
      id: hub.id,
      name: hub.name,
      popupOpenId: null,
    });
  };

  handleDropdownChange = (e, { value }) => {
    this.setState({
      hubAdvisors: value,
    });
  };

  hubPractice() {
    const { advisor, selectedHub } = this.props;

    return (
      advisor.practices.find((p) => {
        return p.id === selectedHub.practice;
      }) || {}
    );
  }

  myPracticeHubs() {
    const { advisor } = this.props;
    const { hubs = [] } = this.hubPractice();

    const advisorHubIds = advisor.hubs.map(({ id }) => {
      return id;
    });

    return hubs.filter((h) => {
      return advisorHubIds.includes(h.id);
    });
  }

  render() {
    const {
      hubAdvisors,
      hubModalOpen,
      loadingHubTable,
      isEditingHub,
      name,
      popupOpenId,
    } = this.state;

    const { advisor } = this.props;
    const hubs = this.myPracticeHubs();

    return (
      <div style={{ marginTop: "1em" }}>
        <Header as="h1">Hub Information</Header>
        <div>
          {loadingHubTable ? (
            <Placeholder
              style={{ marginBottom: "1em" }}
              data-test="hub-information-table-loader"
            >
              <Placeholder.Header>
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder.Header>
            </Placeholder>
          ) : (
            <Table
              selectable={advisor.role.title === "Administrator"}
              stackable
              data-test="hub-information-table"
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Members</Table.HeaderCell>
                  <Access required={["Administrator"]}>
                    <Table.HeaderCell />
                  </Access>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {hubs
                  .sort((a, b) => {
                    return a.createdAt < b.createdAt;
                  })
                  .map((hub) => {
                    return (
                      <Table.Row key={hub.id}>
                        <Table.Cell>
                          <Link to={`/hub/${hub.hubId}/settings/hub`}>
                            {hub.name}
                          </Link>
                        </Table.Cell>
                        <Table.Cell>
                          {hub.advisors.map((hubAdvisor) => {
                            return (
                              <Label
                                content={hubAdvisor.firstName}
                                icon="user circle"
                                key={hubAdvisor.id}
                                style={{ margin: "1px 2px 1px 0" }}
                              />
                            );
                          })}
                        </Table.Cell>
                        <Access required={["Administrator"]}>
                          <Table.Cell>
                            <PopupMenu
                              trigger={
                                <Icon
                                  color="grey"
                                  link
                                  name="ellipsis horizontal"
                                  onClick={() => {
                                    this.setState({ popupOpenId: hub.id });
                                  }}
                                  style={{ float: "right" }}
                                />
                              }
                              buttons={[
                                {
                                  content: "Edit Hub",
                                  icon: "edit",
                                  onClick: () => {
                                    this.openHubModal(hub);
                                  },
                                },
                              ]}
                              open={popupOpenId === hub.id}
                              onClose={() => {
                                this.setState({ popupOpenId: null });
                              }}
                            />
                          </Table.Cell>
                        </Access>
                      </Table.Row>
                    );
                  })}
              </Table.Body>
            </Table>
          )}
          <FeatureFlag id="add-new-hub">
            <Access required={["Administrator"]}>
              <AddHub onDone={this.reloadData} />
            </Access>
          </FeatureFlag>
        </div>
        <Modal
          onClose={() => {
            this.setState({ id: null, hubAdvisors: [], hubModalOpen: false });
          }}
          size="mini"
          open={hubModalOpen}
          closeOnDimmerClick={false}
          style={{ minWidth: "360px" }}
        >
          <Modal.Header>Edit Hub Information</Modal.Header>
          <Modal.Content>
            <Modal.Description>
              <Form>
                <Form.Field>
                  <label>Hub Name</label>
                  <Input
                    name="name"
                    value={name}
                    onChange={this.handleChange}
                    autoComplete="off"
                  />
                </Form.Field>
                {hubs.length > 1 && (
                  <Form.Field>
                    <label>Advisors</label>
                    <Dropdown
                      fluid
                      multiple
                      onChange={this.handleDropdownChange}
                      options={uniqBy(
                        this.hubPractice()?.advisors || [],
                        "id"
                      ).map((a) => {
                        return {
                          key: a.id,
                          text: `${a.firstName} ${a.lastName}`,
                          value: a.id,
                        };
                      })}
                      placeholder="Advisors"
                      selection
                      value={hubAdvisors}
                    />
                  </Form.Field>
                )}
              </Form>
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button
              content="Discard Changes"
              basic
              disabled={isEditingHub}
              onClick={() => {
                this.setState({ hubModalOpen: false });
              }}
            />
            <Button
              positive
              content="Save Changes"
              onClick={this.save}
              disabled={isEditingHub}
              loading={isEditingHub}
            />
          </Modal.Actions>
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    selectedHub: state.hubly.data.hub.selected.hub,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateAdvisor: (id, request, callback) => {
      dispatch(UpdateAdvisor(id, request, callback));
    },
    setAdvisor: (advisor) => {
      dispatch(SetAdvisor(advisor));
    },
    setAlert: (alert) => {
      dispatch(SetAlert(alert));
    },
    setHub: (hub) => {
      dispatch(SetHub(hub));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HubInformationForm);
